import { Alert, Box, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTimeRangeFromQuery } from "../hooks/useRouteParams";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import { TimeRangeFilterBar } from "../components/filters/TimeRangeFilterBar";
import { FilterBar } from "../components/filters/FilterBar";
import { RefreshControls } from "../components/filters/RefreshControls";
import { HealthChecksFilter } from "../components/filters/HealthChecksFilter";
import { Panel } from "../components/layout/Panel";
import { TimeSeriesLineChart } from "../components/charts/TimeSeriesLineChart";
import {
  getHealthProbes,
  getHealthProbeRun,
  type HealthProbePoint,
  type HealthProbeRunDetail,
} from "../api/client";
import { PageErrorState } from "../components/pages/PageErrorState";
import { PageLoadingState } from "../components/pages/PageLoadingState";
import { TimelineInteractionProvider } from "../interaction/TimelineInteractionContext";
import { getHealthCheckColor } from "../utils/colors";
import { useNavigate, useSearchParams } from "react-router-dom";

export interface HealthPointMeta {
  key: string;
  serviceSlug: string;
}

/** Renders subsystem status for a probe run in the tooltip. Must be a real component so hooks run consistently when chart payload count changes. */
function HealthTooltipExtra({ responseId }: { responseId: string }) {
  const [currentProbeRun, setCurrentProbeRun] = useState<HealthProbeRunDetail | null>(null);

  useEffect(() => {
    if (!responseId) return;

    const abort = new AbortController();
    getHealthProbeRun(responseId, abort.signal)
      .then((run) => {
        if (abort.signal.aborted) return;
        setCurrentProbeRun(run);
      })
      .catch(() => {
        if (abort.signal.aborted) return;
        setCurrentProbeRun(null);
      });
    return () => abort.abort();
  }, [responseId]);

  // if (probesAggregated) {
  //   return (
  //     <Box>
  //       <Alert severity="warning">
  //         Health probes are aggregated. Please select a specific health check to view the data.
  //       </Alert>
  //     </Box>
  //   );
  // }

  if (!currentProbeRun?.probes?.length) return null;

  const subsystems = currentProbeRun.probes.filter(
    (pr) => pr.probe_name.startsWith("subsystem_") && pr.probe_name.endsWith("_health_status"),
  );
  if (subsystems.length === 0) return null;

  return (
    <Box sx={{ mt: 0.75, pt: 0.75, borderTop: 1, borderColor: "divider" }}>
      <Box component="ul" sx={{ m: 0, pl: 1.5, fontSize: "0.75rem" }}>
        {subsystems.map((s) => {
          const status = (s.probe_metadata as { status?: string })?.status ?? "—";
          const name = s.probe_name.replace(/^subsystem_|_health_status$/g, "");
          return (
            <li key={s.id}>
              {name}: {status}
            </li>
          );
        })}
      </Box>
    </Box>
  );
}

export function PageServiceHealth() {
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { range, setRange } = useTimeRangeFromQuery();

  const [refreshToken, setRefreshToken] = useState(0);

  const [probes, setProbes] = useState<HealthProbePoint[]>([]);
  const [probesAggregation, setProbesAggregation] = useState<{
    type: string;
    interval?: string;
  }>({ type: "none" });
  const [probesLoading, setProbesLoading] = useState(false);
  const [probesError, setProbesError] = useState<string | null>(null);

  // Load probes for current time range
  useEffect(() => {
    if (!serviceSlug || !range) return;

    const abort = new AbortController();

    setProbesLoading(true);
    setProbesError(null);

    getHealthProbes(
      serviceSlug,
      {
        start_time: range.from.toISOString(),
        end_time: range.to.toISOString(),
      },
      abort.signal,
    )
      .then((result) => {
        if (abort.signal.aborted) return;
        setProbes(result.data);
        setProbesAggregation(result.aggregation);
      })
      .catch((err) => {
        if (abort.signal.aborted) return;
        setProbesError(err instanceof Error ? err.message : "Failed to load health probes");
      })
      .finally(() => {
        if (abort.signal.aborted) return;
        setProbesLoading(false);
      });

    return () => abort.abort();
  }, [serviceSlug, range, refreshToken]);

  const { responseTimeSeries, statusSeries, lineNames } = useMemo(() => {
    if (probes.length === 0)
      return { responseTimeSeries: [], statusSeries: [], lineNames: [] as string[] };

    const chartByCheck = new Map<
      string,
      {
        name: string;
        color: string;
        statusPoints: {
          ts: Date;
          value: number | null;
          key?: string;
        }[];
        responseTimePoints: {
          ts: Date;
          value: number | null;
          key?: string;
        }[];
      }
    >();

    for (const p of probes) {
      const key = p.health_check_name;
      const ts = new Date(p.observed_at);

      ts.setMilliseconds(0);

      const entry = chartByCheck.get(key) ?? {
        name: p.health_check_name,
        color: getHealthCheckColor(p.health_check_name),
        statusPoints: [],
        responseTimePoints: [],
      };
      entry.statusPoints.push({
        ts: ts,
        key: p.response_id,
        value: statusSortValue(p.status),
      });
      entry.responseTimePoints.push({
        ts: ts,
        key: p.response_id,
        value: p.response_time_ms != null ? p.response_time_ms / 1000 : null,
      });
      chartByCheck.set(key, entry);
    }

    const responseTimeSeries = Array.from(chartByCheck.entries()).map(([id, entry]) => ({
      id,
      label: entry.name,
      color: entry.color,
      points: entry.responseTimePoints.sort((a, b) => a.ts.getTime() - b.ts.getTime()),
    }));
    const statusSeries = Array.from(chartByCheck.entries()).map(([id, entry]) => ({
      id,
      label: entry.name,
      color: entry.color,
      points: entry.statusPoints.sort((a, b) => a.ts.getTime() - b.ts.getTime()),
    }));

    return {
      responseTimeSeries,
      statusSeries,
      lineNames: Array.from(chartByCheck.keys()),
    };
  }, [probes, serviceSlug]);

  const healthCheckNamesSelected = useMemo(() => {
    const raw = searchParams.get("health_checks");
    if (!raw) return [];
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }, [searchParams]);

  const filteredResponseTimeSeries = useMemo(() => {
    if (healthCheckNamesSelected.length === 0) return responseTimeSeries;
    return responseTimeSeries.filter((s) => healthCheckNamesSelected.includes(s.id));
  }, [responseTimeSeries, healthCheckNamesSelected]);

  const filteredStatusSeries = useMemo(() => {
    if (healthCheckNamesSelected.length === 0) return statusSeries;
    return statusSeries.filter((s) => healthCheckNamesSelected.includes(s.id));
  }, [statusSeries, healthCheckNamesSelected]);

  const handlePointClick = (point: { ts: number; key?: string }) => {
    if (!point.key) {
      return;
    }

    navigate(
      `/services/${encodeURIComponent(serviceSlug!)}/health/res/${encodeURIComponent(point.key)}`,
    );
  };

  const tooltipExtraContent = (props: { key: string }) =>
    props.key ? <HealthTooltipExtra responseId={props.key} /> : null;

  if (!serviceSlug) {
    return (
      <Box>
        <Alert severity="warning">
          No Service in URL. Please navigate from a product’s services list.
        </Alert>
      </Box>
    );
  }

  return (
    <TimelineInteractionProvider>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
        <Panel>
          <FilterBar>
            <TimeRangeFilterBar />
            <HealthChecksFilter options={lineNames} label="Health checks" />
            <Box data-filterbar-wide="true" sx={{ maxWidth: "none", minWidth: "auto" }}>
              <RefreshControls setRefreshToken={setRefreshToken} isFetching={probesLoading} />
            </Box>
          </FilterBar>
          {probesError ? <PageErrorState message={probesError} sx={{ mt: 1.5 }} /> : null}
          {probesLoading ? (
            <PageLoadingState message="Loading health probes..." sx={{ mt: 1.5 }} />
          ) : null}
        </Panel>

        <Box sx={{ width: "100%" }}>
          <Panel
            title="Response time"
            subtitle="s"
            toolbar={
              <Typography
                variant="caption"
                sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
              >
                {probesAggregation.type !== "none" && probesAggregation.interval
                  ? `${probesAggregation.interval} ${probesAggregation.type}`
                  : "No aggregation"}
              </Typography>
            }
          >
            {filteredResponseTimeSeries.length === 0 ? (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                No probe data available for the selected time range.
              </Typography>
            ) : (
              <TimeSeriesLineChart
                syncId="health-chart"
                series={filteredResponseTimeSeries}
                height={260}
                xRange={range}
                onPointClick={handlePointClick}
                onRangeSelect={setRange}
              />
            )}
          </Panel>
          <Box sx={{ mt: 2 }}>
            <Panel
              title="Status"
              subtitle="{status}"
              toolbar={
                <Typography
                  variant="caption"
                  sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
                >
                  {probesAggregation.type !== "none" && probesAggregation.interval
                    ? `${probesAggregation.interval} ${probesAggregation.type}`
                    : "No aggregation"}
                </Typography>
              }
            >
              {filteredStatusSeries.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  No status data available for the selected time range.
                </Typography>
              ) : (
                <TimeSeriesLineChart
                  syncId="health-chart"
                  series={filteredStatusSeries}
                  yLabel="Status"
                  height={200}
                  xRange={range}
                  tooltipExtraContent={(props) => tooltipExtraContent(props)}
                  onPointClick={handlePointClick}
                  onRangeSelect={setRange}
                />
              )}
            </Panel>
          </Box>
        </Box>
      </Box>
    </TimelineInteractionProvider>
  );
}

function statusSortValue(status: string): number {
  switch (status) {
    case "healthy":
      return 2;
    case "degraded":
      return 1;
    case "unhealthy":
      return 0;
    default:
      return -1;
  }
}
